import * as React from 'react';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Checkbox, Col, Input, Row, Spin, Divider } from 'antd';
import ReactQuill from 'react-quill';

import { imageUrl } from '../../../config/config';
import AttributeQuery from '../../../components/Query/Attributes';

import { Title } from '../../../components/Layout/components';
import Form, { FormItem, UploadInput } from '../../../components/Form';
import HeurekaSelectCategory from '../../../modules/Heureka/SelectCategory';

interface ICategoryDetailProps extends RouteComponentProps {
  domain: string;
  category: GQL.ICategory;
  parentCategoryId?: string;
  onSave: (category: GQL.ICategory) => void;
  onCancel: () => void;
  languages: Array<GQL.ILanguage>;
}

interface ICategoryDetailState {
  category: GQL.ICategory;
  currentLanguage: string;
}

const EnsureSlug = gql`
  query slugAvailable($language: String!, $value: String, $domain: String!, $categoryId: String) {
    slugAvailable(language: $language, value: $value, domain: $domain, categoryId: $categoryId)
  }
`;

class CategoryDetail extends React.Component<ICategoryDetailProps, ICategoryDetailState> {

  constructor(props: ICategoryDetailProps) {
    super(props);

    this.state = {
      category: { ...props.category },
      currentLanguage: props.languages.map(l => l.value)[0],
    };

    this.onChange = this.onChange.bind(this);
    this.updateAttributes = this.updateAttributes.bind(this);
    this.save = this.save.bind(this);
  }

  public render() {
    const { languages } = this.props;
    const { category, currentLanguage } = this.state;

    return (
      <div>
        <Title
          languages={languages}
          currentLanguage={currentLanguage}
          onChange={(l: string) => { this.setState({ currentLanguage: l }); }}
        >
          {category
            ? `Category ${category.title ? `"${category.title}"` : ''}`
            : `Category`}
        </Title>

        <Divider />
        <Form
          onSubmit={this.save}
          onCancel={this.props.onCancel}
        >
          <Row>
            <Col span={12}>
              <FormItem label={'Title'} required={true}>
                <Input
                  name={'title'}
                  autoFocus={true}
                  addonBefore={'Internal'}
                  placeholder="Internal name. For example &quot;Shoes&quot;"
                  value={this.state.category.title}
                  required={true}
                  onChange={this.onChange}
                />
              </FormItem>
              <FormItem label={`Name`} required={true}>
                <Input
                  name={'name'}
                  placeholder="Category's title in the eshop"
                  addonBefore={this.state.currentLanguage}
                  value={(this.state.category.name || {})[this.state.currentLanguage]}
                  required={true}
                  onChange={e => this.onChange(e, this.state.currentLanguage)}
                />
              </FormItem>
              <FormItem label={`Long name`}>
                <Input
                  name={'longName'}
                  placeholder="Category's title in the category page"
                  addonBefore={this.state.currentLanguage}
                  value={(this.state.category.longName || {})[this.state.currentLanguage]}
                  onChange={e => this.onChange(e, this.state.currentLanguage)}
                />
              </FormItem>
              <Query
                query={EnsureSlug}
                variables={{
                  language: this.state.currentLanguage,
                  value: (this.state.category.slug || {})[this.state.currentLanguage],
                  domain: this.props.domain,
                  categoryId: this.state.category.id
                }}
                fetchPolicy={'network-only'}
              >
                {() => {
                  return (
                    <FormItem
                      label={`Slug`}
                    // hasFeedback={true}
                    >
                      <Input
                        name={'slug'}
                        placeholder="Category slug in the eshop"
                        addonBefore={this.state.currentLanguage}
                        value={
                          (this.state.category.slug || {})[this.state.currentLanguage]
                        }
                        onChange={e => this.onChange(e, this.state.currentLanguage)}
                      />
                    </FormItem>
                  );
                }}
              </Query>
              <FormItem label={`Description - ${this.state.currentLanguage}`}>
                <Input.TextArea
                  name={'description'}
                  placeholder="Describe your category here"
                  value={(this.state.category.description || {})[this.state.currentLanguage]}
                  onChange={e => this.onChange(e, this.state.currentLanguage)}
                />
              </FormItem>
              <FormItem label={`Long Description - ${this.state.currentLanguage}`} >
                <ReactQuill
                  key={`long-description-${this.state.currentLanguage}`}
                  placeholder="Type short introduction of product..."
                  value={(this.state.category.longDescription || {})[this.state.currentLanguage] || ''}
                  onChange={html => this.onChange({ target: { name: 'longDescription', value: html }}, this.state.currentLanguage)}
                />
              </FormItem>
              <FormItem label={`Category in Heureka.cz`}>
                <HeurekaSelectCategory
                  name={'heurekaCategory'}
                  value={this.state.category.heurekaCategory}
                  onChange={this.onChange}
                />
              </FormItem>
              <FormItem label={'Priority'} extra={'Main categories with a priority higher than 100 will not appear in the header'}>
                <Input
                  name={'priority'}
                  placeholder="Priority"
                  type={'number'}
                  defaultValue={(this.state.category.priority || '').toString()}
                  onChange={this.onChange}
                />
              </FormItem>
              <FormItem>
                <Checkbox
                  name={'active'}
                  checked={this.state.category.active !== undefined ? this.state.category.active : true}
                  onChange={e => this.onChange(e)}
                >
                  Active
                </Checkbox>
              </FormItem>

              <FormItem label={'Image'}>
                <UploadInput
                  accept={'image/*'}
                  action={`${imageUrl}/images`}
                  listType="picture-card"
                  value={this.state.category.image || null}
                  onChange={(value: any) => this.onChange({ target: { name: 'image', value } })}
                />
              </FormItem>
            </Col>
            <Col offset={1} md={11}>
              <FormItem label={`Meta title`}>
                <Input
                  name={'metaTitle'}
                  placeholder="Meta title (SEO)"
                  addonBefore={this.state.currentLanguage}
                  value={(this.state.category.metaTitle || {})[this.state.currentLanguage]}
                  required={false}
                  onChange={e => this.onChange(e, this.state.currentLanguage)}
                />
              </FormItem>
              <FormItem label={`Meta description - ${this.state.currentLanguage}`}>
                <Input.TextArea
                  name={'metaDescription'}
                  placeholder="Describe category without links and maximum length 160 characters here"
                  value={(this.state.category.metaDescription || {})[this.state.currentLanguage]}
                  onChange={e => this.onChange(e, this.state.currentLanguage)}
                />
              </FormItem>


              {/* <h2>Banner</h2> */}
              <FormItem label={`Banner - ${currentLanguage}`}>
                <UploadInput
                  accept={'image/*'}
                  action={`${imageUrl}/images`}
                  listType="picture-card"
                  value={(this.state.category.banner || {})[currentLanguage]}
                  onChange={(value: any) => this.onChange({ target: { name: 'banner', value } }, currentLanguage)}
                />
              </FormItem>
              <FormItem label={'Banner link'}>
                <Input
                  name={'bannerLink'}
                  disabled={!(this.state.category.banner || {})[currentLanguage]}
                  addonBefore={this.state.currentLanguage}
                  placeholder={`Banner link, like /hello-world (without domain)`}
                  value={(this.state.category.bannerLink || {})[currentLanguage]}
                  onChange={(e: any) => this.onChange(e, currentLanguage)}
                />
              </FormItem>

              <h2>Category attributes settings</h2>
              <FormItem>
                <p style={{ lineHeight: 1 }}>
                  <i>
                    Select attributes, that will be available for category filters and will be
                    automatically offered in products assigned to this category
                  </i>
                </p>
                <AttributeQuery>
                  {(query: any) => {
                    if (query.attributes.loading || !query.attributes.attributes) {
                      return (
                        <div>
                          <Spin /> Loading attributes ...
                        </div>
                      );
                    }
                    return (
                      <Checkbox.Group
                        value={(this.state.category.attributes || []).map(
                          (value: any) => value.id
                        )}
                        onChange={checked => {
                          this.updateAttributes(checked, query.attributes.attributes);
                        }}
                      >
                        {query.attributes.attributes.map(
                          (attribute: any, index: number) => (
                            <Col span={24} key={index}>
                              <Checkbox value={attribute.id}>
                                {attribute.title}
                              </Checkbox>
                            </Col>
                          )
                        )}
                      </Checkbox.Group>
                    );
                  }}
                </AttributeQuery>
              </FormItem>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }

  private updateAttributes(checked: any, attributes: any): void {
    const nextAttrs = attributes.filter((attr: any) => {
      return checked.indexOf(attr.id) > -1;
    });

    this.onChange({
      target: {
        name: 'attributes',
        value: nextAttrs
      }
    });
  }

  private onChange(e: any, language?: string) {
    const { name, value, checked } = e.target;

    const inputValue = (value !== undefined ? value : checked);
    const newValue = (language
      ? { [name]: { ...this.state.category[name], [language]: inputValue } }
      : { [name]: inputValue });
    this.setState({
      category: { ...this.state.category, ...newValue }
    });
  }

  private async save() {
    this.props.onSave(this.state.category);
  }
}

export default withRouter(CategoryDetail);



// WEBPACK FOOTER //
// ./src/scenes/Categories/CategoryDetail/CategoryDetail.tsx